import get from "lodash/get";

import { LedgerActivityRecord } from "../../reducers/loyalty/ledgerActivity";
import { CouponRecord } from "../../reducers/reward/coupons";
import { RewardRecord } from "../../reducers/reward/rewards";

/**
 * Reward-store redemptions and advocate ledger grants earmarked for the reward
 * store should not appear on the loyalty dashboard history.
 */
export function isRewardStoreRewardRecord(reward: RewardRecord): boolean {
  const tx = get(reward, "transaction") as
    | { rewardStoreId?: string; rewardStoreWidgetId?: string }
    | undefined;
  if (tx?.rewardStoreId || tx?.rewardStoreWidgetId) {
    return true;
  }
  if (get(reward, "reward.rewardStoreReward") === true) {
    return true;
  }
  return false;
}

export function rewardRecordCouponCode(
  reward: RewardRecord
): string | undefined {
  const code = get(reward, "coupon.id") || get(reward, "definition.code");
  return typeof code === "string" && code.trim() ? code : undefined;
}

export function filterLoyaltyDashboardRewards(
  rewards: RewardRecord[] | null | undefined
): RewardRecord[] {
  if (!rewards?.length) {
    return rewards ? [...rewards] : [];
  }
  return rewards.filter((r) => !isRewardStoreRewardRecord(r));
}

/**
 * Drops coupon codes that appear only on reward-store reward rows (same GET
 * /rewards payload used for MyRewards). Does not classify referral vs loyalty
 * codes—product can revisit if the API exposes campaign on each coupon.
 */
export function filterLoyaltyDashboardCoupons(
  coupons: CouponRecord[] | null | undefined,
  rewardsForCodeLookup: RewardRecord[] | null | undefined
): CouponRecord[] {
  if (!coupons?.length) {
    return coupons ? [...coupons] : [];
  }
  const exclude = new Set<string>();
  for (const r of rewardsForCodeLookup || []) {
    if (!isRewardStoreRewardRecord(r)) {
      continue;
    }
    const code = rewardRecordCouponCode(r);
    if (code) {
      exclude.add(code.toLowerCase());
    }
  }
  if (exclude.size === 0) {
    return [...coupons];
  }
  return coupons.filter((c) => !exclude.has(c.coupon.toLowerCase()));
}

/**
 * Filters ledger activity rows that belong to the reward store.
 *
 * Two signals are used:
 *  - Grant entries (creditSource "reward"): target.id matches a reward-store
 *    reward ID in the rewards list.
 *  - Redemption entries: sourceName matches the exchangerName of a reward-store
 *    reward in the rewards list.
 *
 * The rewards list serves as the source of truth for which exchanger names and
 * reward IDs belong to the reward store.
 */
export function filterLoyaltyDashboardLedgerActivity(
  ledgerActivity: LedgerActivityRecord[] | null | undefined,
  rewardsForLookup: RewardRecord[] | null | undefined
): LedgerActivityRecord[] {
  if (!ledgerActivity?.length) {
    return ledgerActivity ? [...ledgerActivity] : [];
  }

  const rewardStoreRewardIds = new Set<string>();
  const rewardStoreExchangerNames = new Set<string>();

  for (const r of rewardsForLookup ?? []) {
    if (!isRewardStoreRewardRecord(r)) {
      continue;
    }
    rewardStoreRewardIds.add(r.id);
    const exchangerName = get(r, "transaction.exchangerName");
    if (typeof exchangerName === "string" && exchangerName.trim()) {
      rewardStoreExchangerNames.add(exchangerName.toLowerCase());
    }
  }

  if (rewardStoreRewardIds.size === 0 && rewardStoreExchangerNames.size === 0) {
    return [...ledgerActivity];
  }

  return ledgerActivity.filter((entry) => {
    const targetId = get(entry, "target.id") as string | undefined;
    if (targetId && rewardStoreRewardIds.has(targetId)) {
      return false;
    }
    if (
      entry.sourceName &&
      rewardStoreExchangerNames.has(entry.sourceName.toLowerCase())
    ) {
      return false;
    }
    return true;
  });
}

export function loyaltyDashboardFilteredRewardsAndCoupons(params: {
  rewardStoreWidgetId?: string | null;
  rewards: RewardRecord[] | null | undefined;
  coupons: CouponRecord[] | null | undefined;
}): {
  rewards: RewardRecord[] | null | undefined;
  coupons: CouponRecord[] | null | undefined;
} {
  const { rewardStoreWidgetId, rewards, coupons } = params;
  if (rewardStoreWidgetId) {
    return { rewards, coupons };
  }
  return {
    rewards:
      rewards == null ? rewards : filterLoyaltyDashboardRewards(rewards),
    coupons:
      coupons == null
        ? coupons
        : filterLoyaltyDashboardCoupons(coupons, rewards ?? undefined),
  };
}
